import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import catalogService from '../utils/catalogService';

const SelectField = ({ field, value, onChange, error, readonly }) => {
  const { id, name, label, required, catalogReference, hint } = field;
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  
  // Cargar las opciones del catálogo de forma asíncrona
  useEffect(() => {
    const loadOptions = async () => {
      if (!catalogReference) {
        setOptions([]);
        return;
      }
      
      setIsLoading(true);
      try {
        const catalogOptions = await catalogService.getOptions(catalogReference);
        setOptions(catalogOptions);
      } catch (error) {
        // console.error('Error al cargar opciones del catálogo:', error);
        setOptions([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadOptions();
  }, [catalogReference]);
  
  const handleChange = (selectedOption) => {
    const selectedValue = selectedOption ? selectedOption.value : null;
    onChange(name, selectedValue);
  };

  const fieldId = `select_${id || name}`;
  const hasError = error && error.length > 0;

  // Convertir opciones al formato de react-select
  const selectOptions = options.map(option => ({
    value: option.originalId,
    label: option.label
  }));

  // Efecto para actualizar la opción seleccionada cuando cambien las opciones o el valor
  useEffect(() => {
    if (options.length === 0) {
      setSelectedOption(null);
      return;
    }
    
    // Convertir opciones al formato de react-select
    const selectOptions = options.map(option => ({
      value: option.originalId,
      label: option.label
    }));
        
    // Comparación flexible que maneja diferentes tipos de datos
     const foundOption = selectOptions.find(option => {
       // Comparación estricta primero con value
       if (option.value === value) return true;
       // Comparación flexible para strings y números con value
       if (String(option.value) === String(value)) return true;
       // Comparación numérica si ambos pueden ser números con value
       if (!isNaN(option.value) && !isNaN(value) && Number(option.value) === Number(value)) return true;
       
       // Si no coincide con value, intentar con originalId (para catálogos)
       const originalOption = options.find(opt => opt.value === option.value);
       if (originalOption && originalOption.originalId) {
         if (originalOption.originalId === value) return true;
         if (String(originalOption.originalId) === String(value)) return true;
         if (!isNaN(originalOption.originalId) && !isNaN(value) && Number(originalOption.originalId) === Number(value)) return true;
       }
       
       return false;
     }) || null;
    
     setSelectedOption(foundOption);
  }, [options, value, name]);

  // Estilos personalizados para react-select
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: hasError ? '#dc3545' : state.isFocused ? '#007bff' : '#ced4da',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none',
      '&:hover': {
        borderColor: hasError ? '#dc3545' : '#007bff'
      }
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#6c757d'
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999
    })
  };

  return (
    <div className="form-field select-field">
      <Select
        id={fieldId}
        name={name}
        value={selectedOption}
        onChange={handleChange}
        options={selectOptions}
        placeholder={isLoading ? "Cargando opciones..." : `Seleccionar`}
        isClearable
        isSearchable
        isLoading={isLoading}
        isDisabled={isLoading || readonly}
        styles={customStyles}
        className={hasError ? 'react-select-error' : ''}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        noOptionsMessage={() => 'No hay opciones disponibles'}
        loadingMessage={() => 'Cargando opciones...'}
      />
      
      
      {catalogReference && !isLoading && options.length === 0 && (
        <div className="field-warning">
          ⚠️ Catálogo '{catalogReference}' no encontrado o vacío
        </div>
      )}
      
      <style>{`
        .form-field {
          margin-bottom: 10px;
        }
        
        .field-label {
          display: block;
          font-weight: 600;
          color: #333;
          font-size: 14px;
        }
        
        .field-label.required {
          color: #1976d2;
        }
        
        .required-indicator {
          color: #d32f2f;
        }
        
        .field-hint {
          font-size: 12px;
          color: #666;
          margin-bottom: 8px;
          font-style: italic;
        }
        
        .form-select {
          width: 100%;
          padding: 12px;
          border: 2px solid #e0e0e0;
          border-radius: 6px;
          font-size: 14px;
          background-color: white;
          transition: border-color 0.2s ease;
        }
        
        .form-select:focus {
          outline: none;
          border-color: #1976d2;
          box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1);
        }
        
        .form-select.error {
          border-color: #d32f2f;
        }
        
        .form-select.error:focus {
          border-color: #d32f2f;
          box-shadow: 0 0 0 3px rgba(211, 47, 47, 0.1);
        }
        
        .field-error {
          color: #d32f2f;
          font-size: 12px;
          margin-top: 4px;
          display: flex;
          align-items: center;
        }
        
        .field-error::before {
          content: '⚠️';
          margin-right: 4px;
        }
        
        .field-warning {
          color: #f57c00;
          font-size: 12px;
          margin-top: 4px;
          padding: 8px;
          background-color: #fff3e0;
          border-radius: 4px;
          border-left: 4px solid #f57c00;
        }
        
        .select-field {
          position: relative;
        }
      `}</style>
    </div>
  );
};

export default SelectField;